import { useCallback, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { Toast } from '@/components/Toast'
import { ID } from '@/types/common'
import classRecordApi from '../api'
import {
  createQuickCreateRow,
  defaultClassRecordRules,
  QuickCreateItem,
  toQuickCreateItem,
} from '../page-types'
import {
  BillingMode,
  ClassRecordKind,
  ClassRecordRules,
  ClassType,
  StudentClassCurrentConfig,
} from '../types'

type UseClassRecordQuickCreateOptions = {
  currentConfig: StudentClassCurrentConfig | null
  rules: ClassRecordRules | null
  selectedStudentId: ID | undefined
  loadRecords: () => Promise<void>
}

export function useClassRecordQuickCreate({
  currentConfig,
  rules,
  selectedStudentId,
  loadRecords,
}: UseClassRecordQuickCreateOptions) {
  const effectiveRules = rules ?? defaultClassRecordRules()
  const [quickCreateModalOpen, setQuickCreateModalOpen] = useState(false)
  const [quickCreateDate, setQuickCreateDate] = useState<Dayjs | null>(null)
  const [quickCreateRecordKind, setQuickCreateRecordKind] = useState<ClassRecordKind>(
    ClassRecordKind.MANUAL,
  )
  const [quickCreateItems, setQuickCreateItems] = useState<QuickCreateItem[]>([])
  const [quickCreateSaving, setQuickCreateSaving] = useState(false)

  const handleOpenQuickCreateModal = useCallback(() => {
    if (!currentConfig) {
      return
    }
    setQuickCreateDate(dayjs())
    setQuickCreateRecordKind(ClassRecordKind.MANUAL)
    setQuickCreateItems(
      currentConfig.templates.length > 0
        ? currentConfig.templates.map((template) =>
            toQuickCreateItem(
              template,
              effectiveRules.defaultCoursePrice,
              effectiveRules.defaultClassDurationMinutes,
              ClassRecordKind.MANUAL,
            ),
          )
        : [
            createQuickCreateRow(
              effectiveRules.defaultCoursePrice,
              effectiveRules.defaultClassDurationMinutes,
              ClassRecordKind.MANUAL,
            ),
          ],
    )
    setQuickCreateModalOpen(true)
  }, [currentConfig, effectiveRules.defaultClassDurationMinutes, effectiveRules.defaultCoursePrice])

  const handleQuickCreateRecordKindChange = useCallback(
    (recordKind: ClassRecordKind) => {
      setQuickCreateRecordKind(recordKind)
      setQuickCreateItems((prev) =>
        prev.map((item) => ({
          ...item,
          recordKind,
          coursePrice:
            recordKind === ClassRecordKind.MAKEUP_USAGE ? undefined : item.coursePrice,
        })),
      )
    },
    [],
  )

  const handleQuickCreateItemChange = useCallback(
    (localId: string, next: Partial<QuickCreateItem>) => {
      setQuickCreateItems((prev) =>
        prev.map((item) =>
          item.localId === localId
            ? {
                ...item,
                ...next,
                recordKind: quickCreateRecordKind,
                ...(quickCreateRecordKind === ClassRecordKind.MAKEUP_USAGE
                  ? { coursePrice: undefined }
                  : {}),
              }
            : item,
        ),
      )
    },
    [quickCreateRecordKind],
  )

  const handleQuickCreateAddItem = useCallback(() => {
    setQuickCreateItems((prev) => [
      ...prev,
      createQuickCreateRow(
        effectiveRules.defaultCoursePrice,
        effectiveRules.defaultClassDurationMinutes,
        quickCreateRecordKind,
      ),
    ])
  }, [effectiveRules.defaultClassDurationMinutes, effectiveRules.defaultCoursePrice, quickCreateRecordKind])

  const handleQuickCreateDuplicateItem = useCallback((localId: string) => {
    setQuickCreateItems((prev) => {
      const target = prev.find((item) => item.localId === localId)
      if (!target) {
        return prev
      }
      return [
        ...prev,
        {
          ...target,
          localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      ]
    })
  }, [])

  const handleQuickCreateRemoveItem = useCallback((localId: string) => {
    setQuickCreateItems((prev) => {
      if (prev.length <= 1) return prev
      return prev.filter((item) => item.localId !== localId)
    })
  }, [])

  const getQuickCreateValidationMessage = useCallback(
    (items: QuickCreateItem[]) => {
      if (items.length === 0) {
        return '请至少保留一条手动记录'
      }

      const invalidItem = items.find((item) => {
        if (!item.classType) {
          return true
        }
        if (quickCreateRecordKind === ClassRecordKind.MAKEUP_USAGE && item.classType === ClassType.GROUP) {
          return true
        }
        if (
          quickCreateRecordKind !== ClassRecordKind.MAKEUP_USAGE &&
          item.classType !== ClassType.GROUP &&
          !item.teacherId
        ) {
          return true
        }
        if (!item.durationMinutes || item.durationMinutes <= 0) {
          return true
        }
        if (quickCreateRecordKind === ClassRecordKind.MAKEUP_USAGE) {
          return false
        }
        if (
          item.coursePrice === undefined ||
          item.coursePrice === null ||
          item.coursePrice <= 0
        ) {
          return true
        }
        return false
      })

      if (!invalidItem) {
        return null
      }

      return '请完整填写手动记录：时长必须大于 0；正常记录的非集体课必须选老师；正常记录需填写单价'
    },
    [quickCreateRecordKind],
  )

  const handleSubmitQuickCreate = useCallback(async () => {
    if (!selectedStudentId || !quickCreateDate) {
      Toast.warning('请选择日期')
      return
    }
    const validationMessage = getQuickCreateValidationMessage(quickCreateItems)
    if (validationMessage) {
      Toast.warning(validationMessage)
      return
    }
    try {
      setQuickCreateSaving(true)
      const res = await classRecordApi.manualCreateFromCurrent({
        studentId: selectedStudentId,
        date: quickCreateDate.format('YYYY-MM-DD'),
        items: quickCreateItems.map((item) => ({
          classType: item.classType,
          billingMode: BillingMode.PER_CLASS,
          recordKind: quickCreateRecordKind,
          classRecordStatus: quickCreateRecordKind === ClassRecordKind.MAKEUP_USAGE
            ? undefined
            : item.classRecordStatus,
          durationMinutes:
            item.durationMinutes || effectiveRules.defaultClassDurationMinutes,
          teacherId: item.teacherId,
          coursePrice:
            quickCreateRecordKind === ClassRecordKind.MAKEUP_USAGE
              ? undefined
              : item.coursePrice,
          note: item.note.trim() || undefined,
        })),
      })
      Toast.success(res?.message || '手动记录成功')
      setQuickCreateModalOpen(false)
      await loadRecords()
    } catch (error) {
      Toast.error('记录失败')
      console.error('手动记录失败:', error)
    } finally {
      setQuickCreateSaving(false)
    }
  }, [
    effectiveRules.defaultClassDurationMinutes,
    getQuickCreateValidationMessage,
    loadRecords,
    quickCreateDate,
    quickCreateItems,
    selectedStudentId,
  ])

  return {
    quickCreateModalOpen,
    setQuickCreateModalOpen,
    quickCreateDate,
    setQuickCreateDate,
    quickCreateItems,
    quickCreateSaving,
    quickCreateRecordKind,
    handleOpenQuickCreateModal,
    handleQuickCreateRecordKindChange,
    handleQuickCreateItemChange,
    handleQuickCreateAddItem,
    handleQuickCreateDuplicateItem,
    handleQuickCreateRemoveItem,
    handleSubmitQuickCreate,
  }
}
